/**
 * lib/reports/buildWeeklyReportPayload.ts — Weekly Report: Deterministic Layer
 * ================================================================================
 * EVERY number in the final report comes from this file. Nothing here is
 * LLM-generated. The Cloud Function (functions/src/reports) is only ever
 * handed the JSON this file returns, and is only allowed to write a short
 * intro paragraph using pre-computed LABELS (e.g. "improved" / "declined"),
 * never raw figures — see functions/src/reports/prompt.ts.
 *
 * Reuses, rather than reimplements, the app's already-verified calculation
 * primitives:
 *   - sanitiseWindow, maxDemandKvaOf, loadFactor, detectAnomalies  (lib/calculations, lib/aggregations)
 *   - buildWestBengalBreakdown / buildTelanganaBreakdown           (lib/billing/engines/*)
 *   - tariff.pfAdjustmentPct / tariff.lfRebateRsPerKwh             (tariffs/<state>.ts — never hardcoded)
 *
 * KNOWN GAP (flagged, not silently invented):
 *   "Voltage phase loss under active load" (as seen in the sample Impact
 *   Statements — e.g. "8 events over 18 days, one at 179 kW") has NO existing
 *   detector in lib/calculations.ts's detectAnomalies(). The implementation
 *   below adds a *new* detector (isDropout on a phase AND kwTotal above a
 *   floor) to cover this — review the PHASE_LOSS_MIN_KW threshold below
 *   before trusting this specific finding in production.
 */

import type { TelemetryRow } from '@/types';
import type { StateTariff } from '@/tariffs';
import type { SheetSource } from '@/config/sheets';
import { buildBillBreakdown } from '@/lib/billing';
import {
  safe, mean, max,
  maxDemandKvaOf, loadFactor,
  detectAnomalies, isValidPfReading,
} from '@/lib/calculations';
import { sanitiseWindow } from '@/lib/aggregations';
import {
  capacitorFadeFinding,
  leadingPfFinding,
  pfBasisFinding,
  standbyFloorFinding,
  utilizationFinding,
  voltageThdFinding,
} from '@/lib/features/registerFindings';
import { DEFAULT_THRESHOLDS } from '@/config/constants';
import type { DateWindow } from './weekWindow';

// ─── Small local helpers (verified formula, reimplemented because the ─────────
// ─── source functions are module-private in lib/billing/engines/*.ts) ────────

/** PF for a row window = ΔWh_received / ΔVAh_received (same ratio used by
 *  weightedPF() in both billing engines and registerPF() in calculations.ts). */
function registerPfRatio(rows: TelemetryRow[]): number {
  if (rows.length < 2) {
    const valid = rows.filter(isValidPfReading);
    if (!valid.length) return 0;
    return mean(valid.map(r => safe(r.pfAvg)));
  }
  const sorted = [...rows].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  const first = sorted[0];
  const last  = sorted[sorted.length - 1];
  const dWh   = Math.max(0, safe(last.energyWh)  - safe(first.energyWh));
  const dVah  = Math.max(0, safe(last.energyVAh) - safe(first.energyVAh));
  if (dVah > 0) return Math.min(dWh / dVah, 0.9999);
  const valid = rows.filter(isValidPfReading);
  if (!valid.length) return 0;
  return mean(valid.map(r => safe(r.pfAvg)));
}

function byDate(rows: TelemetryRow[]): Map<string, TelemetryRow[]> {
  const m = new Map<string, TelemetryRow[]>();
  for (const r of rows) {
    const list = m.get(r.date);
    if (list) list.push(r);
    else m.set(r.date, [r]);
  }
  return m;
}

function rupees(n: number): number {
  return Math.round(n * 100) / 100;
}

// ─── Public payload shape ─────────────────────────────────────────────────────

export interface FacilityProfile {
  consumer:        string;
  discomTariff:    string;
  contractDemandKva: number;
  tariffRatesLabel: string;
}

export interface PfSection {
  applicable:       boolean; // false for tariffs with no PF rebate/surcharge table (e.g. TGSPDCL)
  weekAvgPf:        number;
  weekLowPf:        number;
  weekLowPfDate:    string;
  daysBelowThreshold: number;
  /** Number of distinct calendar days with data this week — NOT assumed to be 7 (a partial/short week, or days with no readings, would make "X of 7 days" wrong). */
  daysSpan:         number;
  thresholdUsed:    number;
  actualImpactRs:   number;   // + = net saved, − = net surcharged
  hypotheticalImpactRs: number; // if PF had stayed at tariff.pfTarget all week
  deltaRs:          number;   // hypothetical - actual (what was left on the table)
  allGoodAllWeek:   boolean;
  /**
   * True when the low point is meaningfully below the week's average — computed
   * ONCE here (see PF_MEANINGFUL_DIP) so every consumer (LLM prompt, deterministic
   * fallback) agrees on whether to mention a separate "low point" at all, instead
   * of each re-deriving its own copy of this threshold and risking drift.
   */
  dipIsMeaningful:  boolean;
  narrativeLabel:   'improved' | 'declined' | 'steady' | 'not-applicable';
}

export interface CdUtilizationSection {
  avgUtilizationPct: number;
  avgUtilizationKva: number;
  peakUtilizationPct: number;
  peakUtilizationKva: number;
  contractDemandKva: number;
  exceeded:          boolean;
  excessKva:         number;
  penaltyRs:         number;
  underUtilized:     boolean;  // peak < 80% of CD
  unusedKvaAtPeak:   number;
  narrativeLabel:    'over-limit' | 'under-utilized' | 'healthy';
}

export interface LfSection {
  applicable:        boolean; // false for tariffs with no LF rebate (e.g. TGSPDCL)
  weekAvgLfPct:      number;
  weekLowLfPct:      number;
  weekLowLfDate:     string;
  rebateThresholdPct: number;
  actualImpactRs:    number;
  hypotheticalImpactRs: number;
  deltaRs:           number;
  /** True when the actual outcome already captured (essentially) the full available rebate — nothing more to point out. */
  allGoodAllWeek:    boolean;
  /** Same idea as PfSection.dipIsMeaningful (see LF_MEANINGFUL_DIP below), computed once here. */
  dipIsMeaningful:   boolean;
  /** True when average and low point are both essentially at full utilization (see LF_NEAR_FULL_*) — computed once here. */
  nearFullUtilization: boolean;
  narrativeLabel:    'improved' | 'declined' | 'steady' | 'not-applicable';
}

export interface WeekComparisonSection {
  pfDeltaPts:      number;
  lfDeltaPts:      number;
  cdUtilDeltaPts:  number;
  /** Last week's raw values, so the report can say "from X to Y" instead of just a delta. */
  lastWeekPf:      number;
  lastWeekLfPct:   number;
  lastWeekCdUtilPct: number;
  /** This week's raw values, mirrored here for convenience alongside lastWeek*. */
  thisWeekPf:      number;
  thisWeekLfPct:   number;
  thisWeekCdUtilPct: number;
  /**
   * Net rebate/surcharge from the three billing sections above (PF + LF − CD penalty).
   * Same sign convention as those sections: + = net rebate, − = net surcharge/cost.
   * Only uses amounts already computed for those sections — never invented.
   */
  lastWeekTotalImpactRs: number;
  thisWeekTotalImpactRs: number;
  /** True when at least one of PF / LF / CD contributed a real ₹ amount this or last week. */
  hasRebateSurchargeImpact: boolean;
  /** False when there was no telemetry at all for the prior week — a comparison would just be fabricating a "0 → X" jump, so callers should show a neutral "not enough data yet" message instead. */
  hasLastWeekData: boolean;
  overallLabel:    'improved' | 'declined' | 'mixed';
}

export interface HealthFinding {
  kind:        string;
  title:       string;
  explanation: string;   // fully computed sentence, real numbers only — used verbatim if the LLM is unavailable
  action:      string;   // static recommended-action template for this kind
  /**
   * Every number the LLM may reference when narrating this finding, already
   * computed and formatted here (never invented by the LLM) — label: value.
   */
  stats:       Record<string, string>;
  /** Plain-English context/reasoning facts the LLM may weave in, not raw numbers. */
  narrativeFacts: string[];
}

export interface WeeklyReportPayload {
  generatedAt:   string;
  reportWeekLabel: string;
  facility:      FacilityProfile;
  billing: {
    pf: PfSection;
    cd: CdUtilizationSection;
    lf: LfSection;
    comparison: WeekComparisonSection;
  };
  /** Up to 3 live-anomaly findings, then up to 2 register findings. Ranked within each group. */
  healthFindings: HealthFinding[];
}

// ─── Section builders ─────────────────────────────────────────────────────────

function buildFacilityProfile(sheet: SheetSource, tariff: StateTariff, contractDemandKva: number): FacilityProfile {
  const ratesLabel = tariff.energyUnit === 'kvah'
    ? `Normal ₹${tariff.energyRates.NORMAL.toFixed(2)}/kVAh | Peak ₹${tariff.energyRates.PEAK.toFixed(2)}/kVAh | Off-Peak ₹${tariff.energyRates['OFF-PEAK'].toFixed(2)}/kVAh`
    : `Normal ₹${tariff.energyRates.NORMAL.toFixed(2)}/kWh | Peak ₹${tariff.energyRates.PEAK.toFixed(2)}/kWh | Off-Peak ₹${tariff.energyRates['OFF-PEAK'].toFixed(2)}/kWh`;

  // Taparia 2F (Meter 1) is the Laxmi Garments unit — show them as consumer in the weekly report.
  const consumerName = sheet.id === 'taparia-main'
    ? 'LAXMI GARMENTS'
    : (sheet.displayName ?? sheet.label);
  const consumerId = sheet.meta?.consumerId;

  return {
    consumer: consumerId
      ? `${consumerName} (Consumer ID: ${consumerId})`
      : consumerName,
    discomTariff: `${tariff.discName} | Tariff ${tariff.tariffCategory} | ${tariff.voltageLevel}`,
    contractDemandKva,
    tariffRatesLabel: ratesLabel,
  };
}

/** A PF low point closer to the average than this (absolute PF units) doesn't
 *  count as a real "dip" worth calling out separately — see PfSection.dipIsMeaningful. */
const PF_MEANINGFUL_DIP = 0.02;

function buildPfSection(
  weekRows: TelemetryRow[],
  tariff: StateTariff,
  weeklyBreakdown: ReturnType<typeof buildBillBreakdown>,
): PfSection {
  if (!tariff.hasPfAdjustment) {
    // e.g. TGSPDCL: no per-period PF rebate/surcharge table. Report PF level
    // only — never fabricate a Rs figure the tariff doesn't actually apply.
    const dayMap = byDate(weekRows);
    const dailyPfs = [...dayMap.entries()].map(([date, rows]) => ({ date, pf: registerPfRatio(rows) }));
    const weekAvgPf = weeklyBreakdown.pfWeighted;
    const worst = dailyPfs.reduce((a, b) => (b.pf > 0 && b.pf < (a.pf || 1) ? b : a), dailyPfs[0] ?? { date: '', pf: 0 });
    return {
      applicable: false,
      weekAvgPf,
      weekLowPf: worst?.pf ?? 0,
      weekLowPfDate: worst?.date ?? '',
      daysBelowThreshold: dailyPfs.filter(d => d.pf > 0 && d.pf < tariff.pfPenaltyThreshold).length,
      daysSpan: dailyPfs.length,
      thresholdUsed: tariff.pfPenaltyThreshold,
      actualImpactRs: 0,
      hypotheticalImpactRs: 0,
      deltaRs: 0,
      allGoodAllWeek: dailyPfs.every(d => d.pf === 0 || d.pf >= tariff.pfPenaltyThreshold),
      dipIsMeaningful: weekAvgPf - (worst?.pf ?? 0) > PF_MEANINGFUL_DIP,
      narrativeLabel: 'not-applicable',
    };
  }

  const dayMap = byDate(weekRows);
  const dailyPfs = [...dayMap.entries()].map(([date, rows]) => ({ date, pf: registerPfRatio(rows) }));
  const validDaily = dailyPfs.filter(d => d.pf > 0);
  const weekAvgPf = weeklyBreakdown.pfWeighted;
  const worst = validDaily.length
    ? validDaily.reduce((a, b) => (b.pf < a.pf ? b : a))
    : { date: '', pf: 0 };

  const daysBelowThreshold = validDaily.filter(d => d.pf < tariff.pfPenaltyThreshold).length;

  // Actual: sum of the engine's real per-period PF adjustment for the week.
  // Engine convention: pfAdjTotalRs negative = net saving, positive = net surcharge.
  const actualImpactRs = rupees(-weeklyBreakdown.pfAdjTotalRs);

  // Hypothetical: same energy charges, but PF assumed at tariff.pfTarget all week.
  const hypotheticalImpactRs = rupees(
    weeklyBreakdown.periods.reduce((sum, p) => {
      const pct = tariff.pfAdjustmentPct(tariff.pfTarget, p.period);
      return sum + p.energyChargeRs * (pct / 100);
    }, 0),
  );

  const deltaRs = rupees(hypotheticalImpactRs - actualImpactRs);
  const allGoodAllWeek = daysBelowThreshold === 0 && actualImpactRs >= hypotheticalImpactRs - 1;

  return {
    applicable: true,
    weekAvgPf,
    weekLowPf: worst.pf,
    weekLowPfDate: worst.date,
    daysBelowThreshold,
    daysSpan: dailyPfs.length,
    thresholdUsed: tariff.pfPenaltyThreshold,
    actualImpactRs,
    hypotheticalImpactRs,
    deltaRs,
    allGoodAllWeek,
    dipIsMeaningful: weekAvgPf - worst.pf > PF_MEANINGFUL_DIP,
    narrativeLabel: allGoodAllWeek ? 'steady' : (deltaRs > 1 ? 'declined' : 'improved'),
  };
}

function buildCdSection(weekRows: TelemetryRow[], tariff: StateTariff, contractDemandKva: number): CdUtilizationSection {
  const avgKva = mean(weekRows.map(r => safe(r.kvaTotal)));
  const peakKva = maxDemandKvaOf(weekRows);
  const avgUtilizationPct = contractDemandKva > 0 ? (avgKva / contractDemandKva) * 100 : 0;
  const peakUtilizationPct = contractDemandKva > 0 ? (peakKva / contractDemandKva) * 100 : 0;

  const excessKva = Math.max(0, peakKva - contractDemandKva);
  const exceeded = excessKva > 0;
  // Penal rate differs by tariff model (normal-penal vs single-rate) — never hardcode.
  const penaltyRs = rupees(excessKva * tariff.demandPenalRsKva);

  const underUtilized = !exceeded && peakUtilizationPct < 80;
  const unusedKvaAtPeak = Math.max(0, contractDemandKva - peakKva);

  return {
    avgUtilizationPct: rupees(avgUtilizationPct),
    avgUtilizationKva: rupees(avgKva),
    peakUtilizationPct: rupees(peakUtilizationPct),
    peakUtilizationKva: rupees(peakKva),
    contractDemandKva,
    exceeded,
    excessKva: rupees(excessKva),
    penaltyRs,
    underUtilized,
    unusedKvaAtPeak: rupees(unusedKvaAtPeak),
    narrativeLabel: exceeded ? 'over-limit' : (underUtilized ? 'under-utilized' : 'healthy'),
  };
}

/** Same idea as PF_MEANINGFUL_DIP, but load factor swings are naturally
 *  wider — a gap under this many percentage points isn't worth calling a "dip". */
const LF_MEANINGFUL_DIP_PCT = 5;
/** Average LF at/above this, with the low point within LF_NEAR_FULL_SPAN_PCT of
 *  it, counts as "utilized at full potential all week" (see LfSection.nearFullUtilization). */
const LF_NEAR_FULL_AVG_PCT = 99;
const LF_NEAR_FULL_SPAN_PCT = 3;

function buildLfSection(
  weekRows: TelemetryRow[],
  tariff: StateTariff,
  weeklyBreakdown: ReturnType<typeof buildBillBreakdown>,
): LfSection {
  if (!tariff.hasLfRebate) {
    const dayMap = byDate(weekRows);
    const dailyLfs = [...dayMap.entries()].map(([date, rows]) => ({ date, lf: loadFactor(rows) }));
    const worst = dailyLfs.length ? dailyLfs.reduce((a, b) => (b.lf < a.lf ? b : a)) : { date: '', lf: 0 };
    const weekAvgLfPct = rupees(weeklyBreakdown.lfPct);
    const weekLowLfPct = rupees(worst.lf);
    const nearFullUtilization = weekAvgLfPct >= LF_NEAR_FULL_AVG_PCT && weekAvgLfPct - weekLowLfPct < LF_NEAR_FULL_SPAN_PCT;
    return {
      applicable: false,
      weekAvgLfPct,
      weekLowLfPct,
      weekLowLfDate: worst.date,
      rebateThresholdPct: 0,
      actualImpactRs: 0,
      hypotheticalImpactRs: 0,
      deltaRs: 0,
      allGoodAllWeek: false,
      dipIsMeaningful: !nearFullUtilization && weekAvgLfPct - weekLowLfPct > LF_MEANINGFUL_DIP_PCT,
      nearFullUtilization,
      narrativeLabel: 'not-applicable',
    };
  }

  const dayMap = byDate(weekRows);
  const dailyLfs = [...dayMap.entries()].map(([date, rows]) => ({ date, lf: loadFactor(rows) }));
  const worst = dailyLfs.length ? dailyLfs.reduce((a, b) => (b.lf < a.lf ? b : a)) : { date: '', lf: 0 };

  // Engine convention: lfRebateRs negative = net saving, positive = net surcharge.
  const actualImpactRs = rupees(-weeklyBreakdown.lfRebateRs);

  // Target = top band's lower bound in this tariff's own LF rebate table
  // (derived from tariff config, never a hardcoded number).
  const topBandFloor = tariff.lfRebateTable.length
    ? tariff.lfRebateTable[tariff.lfRebateTable.length - 1].minLfPct
    : 95;
  const hypotheticalImpactRs = rupees(tariff.lfRebateRsPerKwh(topBandFloor) * weeklyBreakdown.totalKwh);
  const deltaRs = rupees(hypotheticalImpactRs - actualImpactRs);

  // First rebate-eligible band's floor, for the "below X% rebate threshold" sentence.
  const rebateThresholdPct = tariff.lfRebateTable.find(r => r.rebateRsPerKwh > 0)?.minLfPct ?? 0;

  // Mirrors PfSection.allGoodAllWeek: if the actual outcome already captured
  // (essentially) the full available rebate, there's nothing left to point
  // out — don't restate the same rupee figure as both "actual" and "hypothetical".
  const allGoodAllWeek = actualImpactRs >= hypotheticalImpactRs - 1;

  const weekAvgLfPct = rupees(weeklyBreakdown.lfPct);
  const weekLowLfPct = rupees(worst.lf);
  const nearFullUtilization = weekAvgLfPct >= LF_NEAR_FULL_AVG_PCT && weekAvgLfPct - weekLowLfPct < LF_NEAR_FULL_SPAN_PCT;

  return {
    applicable: true,
    weekAvgLfPct,
    weekLowLfPct,
    weekLowLfDate: worst.date,
    rebateThresholdPct,
    actualImpactRs,
    hypotheticalImpactRs,
    deltaRs,
    allGoodAllWeek,
    dipIsMeaningful: !nearFullUtilization && weekAvgLfPct - weekLowLfPct > LF_MEANINGFUL_DIP_PCT,
    nearFullUtilization,
    narrativeLabel: deltaRs > 1 ? 'declined' : 'improved',
  };
}

function buildComparisonSection(
  thisWeekPf: number, lastWeekPf: number,
  thisWeekLf: number, lastWeekLf: number,
  thisWeekCdUtil: number, lastWeekCdUtil: number,
  thisWeekTotalImpactRs: number,
  lastWeekTotalImpactRs: number,
  hasRebateSurchargeImpact: boolean,
  hasLastWeekData: boolean,
): WeekComparisonSection {
  // With no telemetry at all for the prior week, lastWeek* is meaningless (0,
  // not "actually zero") — zero out every delta so nothing downstream can
  // fabricate a "went from 0% to 100%" jump out of missing data.
  const pfDeltaPts = hasLastWeekData ? rupees((thisWeekPf - lastWeekPf) * 100) : 0;
  const lfDeltaPts = hasLastWeekData ? rupees(thisWeekLf - lastWeekLf) : 0;
  const cdUtilDeltaPts = hasLastWeekData ? rupees(thisWeekCdUtil - lastWeekCdUtil) : 0;

  const improvements = [pfDeltaPts > 0, lfDeltaPts > 0].filter(Boolean).length;
  const declines     = [pfDeltaPts < 0, lfDeltaPts < 0].filter(Boolean).length;
  const overallLabel: WeekComparisonSection['overallLabel'] =
    improvements > declines ? 'improved' : declines > improvements ? 'declined' : 'mixed';

  return {
    pfDeltaPts, lfDeltaPts, cdUtilDeltaPts,
    lastWeekPf, lastWeekLfPct: rupees(lastWeekLf), lastWeekCdUtilPct: rupees(lastWeekCdUtil),
    thisWeekPf, thisWeekLfPct: rupees(thisWeekLf), thisWeekCdUtilPct: rupees(thisWeekCdUtil),
    lastWeekTotalImpactRs: hasLastWeekData && hasRebateSurchargeImpact ? rupees(lastWeekTotalImpactRs) : 0,
    thisWeekTotalImpactRs: hasRebateSurchargeImpact ? rupees(thisWeekTotalImpactRs) : 0,
    hasRebateSurchargeImpact,
    hasLastWeekData,
    overallLabel,
  };
}

// ─── Health findings (top 3) ──────────────────────────────────────────────────

/**
 * NEW DETECTOR — not present in lib/calculations.ts's detectAnomalies().
 * Flags a row as a "phase loss under active load" event when at least one
 * phase voltage is below the tariff's dropout floor WHILE real load is
 * flowing (kwTotal above PHASE_LOSS_MIN_KW). Review this threshold before
 * relying on this finding — it was not part of the verified codebase.
 */
const PHASE_LOSS_MIN_KW = 5;

function detectPhaseLossEvents(weekRows: TelemetryRow[], tariff: StateTariff): {
  count: number; maxLoadKw: number; dates: Set<string>;
} {
  const sorted = [...weekRows].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  let count = 0;
  let maxLoadKw = 0;
  const dates = new Set<string>();
  let inEvent = false;

  for (const r of sorted) {
    const phaseDown = safe(r.vR) < tariff.voltageDropoutV
                    || safe(r.vY) < tariff.voltageDropoutV
                    || safe(r.vB) < tariff.voltageDropoutV;
    const underLoad = safe(r.kwTotal) > PHASE_LOSS_MIN_KW;
    if (phaseDown && underLoad) {
      if (!inEvent) { count++; inEvent = true; dates.add(r.date); }
      if (r.kwTotal > maxLoadKw) maxLoadKw = r.kwTotal;
    } else {
      inEvent = false;
    }
  }
  return { count, maxLoadKw: rupees(maxLoadKw), dates };
}

/** Rows below this load are treated as idle and excluded from "during active
 *  operation" stats (mean/peak neutral current, imbalance, phase PF, etc.) —
 *  same floor used for phase-loss detection above, kept as its own constant
 *  since the two thresholds could reasonably diverge in future tuning. */
const ACTIVE_LOAD_MIN_KW = 5;

function activeRowsOf(rows: TelemetryRow[]): TelemetryRow[] {
  return rows.filter(r => !r.isDropout && safe(r.kwTotal) > ACTIVE_LOAD_MIN_KW);
}

/** Round to 1 decimal — used for the percentages/means fed to the LLM narrator. */
function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

interface NeutralCurrentStats {
  meanA: number;
  peakA: number;
  pctAboveTenPctPhaseAvg: number;
  /** % of active readings above the flat, tariff-independent 5.0 A warning
   *  threshold (DEFAULT_THRESHOLDS.neutralCurrentA) — the same fixed cutoff
   *  used elsewhere in the app (Alerts page / anomaly detector). */
  pctAboveFlatThreshold: number;
  flatThresholdA: number;
}

/** Neutral current should be near-zero in a balanced, clean 3-phase system —
 *  reported two ways: relative to 10% of that row's average phase current
 *  (physics-based), and against the app-wide flat 5.0 A warning threshold
 *  (DEFAULT_THRESHOLDS.neutralCurrentA) so this reads consistently with the
 *  Alerts page / anomaly detector rather than only a relative percentage. */
function computeNeutralCurrentStats(active: TelemetryRow[]): NeutralCurrentStats | null {
  if (!active.length) return null;
  const vals = active.map(r => safe(r.neutralCurrentA));
  const flatThresholdA = DEFAULT_THRESHOLDS.neutralCurrentA;
  let aboveCount = 0;
  let aboveFlatCount = 0;
  for (const r of active) {
    const phaseAvgI = (safe(r.iR) + safe(r.iY) + safe(r.iB)) / 3;
    if (phaseAvgI > 0 && safe(r.neutralCurrentA) > 0.10 * phaseAvgI) aboveCount++;
    if (safe(r.neutralCurrentA) > flatThresholdA) aboveFlatCount++;
  }
  return {
    meanA: round1(mean(vals)),
    peakA: round1(max(vals)),
    pctAboveTenPctPhaseAvg: round1((aboveCount / active.length) * 100),
    pctAboveFlatThreshold: round1((aboveFlatCount / active.length) * 100),
    flatThresholdA,
  };
}

interface CurrentUnbalanceStats {
  meanPct: number;
  pctAboveThreshold: number;
  thresholdPct: number;
  worstPct: number;
  exceededThreshold: boolean;
  /** Phase currents (A) at the single worst-imbalance reading this week. */
  worstPhaseR: number;
  worstPhaseY: number;
  worstPhaseB: number;
  /** Average of R/Y/B at that same worst reading — (R+Y+B)/3. */
  worstAvgA: number;
  /** Phase with the largest |current − average| deviation at the worst reading. */
  contributingPhase: 'R' | 'Y' | 'B';
  /**
   * True if that phase's ACTIVE POWER also deviated meaningfully from the
   * other phases' power at that same reading — corroborating a real
   * electrical fault (e.g. single-phasing). False means current dropped/spiked
   * on that phase but power didn't move correspondingly, which is the classic
   * signature of a current-sensor (CT) reading fault rather than a real event.
   */
  powerCorroborated: boolean;
  harmonicCorrelated: boolean;
  /**
   * True when the worst reading is an outlier — no other reading this week
   * came anywhere close to it. A single 15-min blip (one phase at/near 0 A
   * while the others carry full load) is much more likely a one-off
   * sensor/data glitch than a real recurring electrical fault, even if power
   * happens to line up with it at that same instant — so it needs its own,
   * more cautious wording rather than being reported as a confirmed event.
   */
  isSingletonEvent: boolean;
}

/** How far a phase's power must deviate from the average phase power (as a
 *  fraction) to count as "corroborating" a current-side fault on that phase. */
const POWER_CORROBORATION_MIN_DEV_RATIO = 0.25;

function powerCorroboratesCurrentFault(row: TelemetryRow, contributingPhase: 'R' | 'Y' | 'B'): boolean {
  const wR = safe(row.wattsR), wY = safe(row.wattsY), wB = safe(row.wattsB);
  const avgW = (wR + wY + wB) / 3;
  if (avgW <= 0) return false;
  const wPhase = contributingPhase === 'R' ? wR : contributingPhase === 'Y' ? wY : wB;
  return Math.abs(wPhase - avgW) / avgW > POWER_CORROBORATION_MIN_DEV_RATIO;
}

/**
 * Correct imbalance formula, recomputed here from raw iR/iY/iB rather than
 * trusting the pipeline's stored `currentUnbalancePct` field — that field is
 * computed in lib/derivedEngine.ts with a confirmed bug (divides by iTotal,
 * the SUM of the three phases, instead of iTotal/3 the AVERAGE — see the
 * field's own doc comment in types/index.ts: "Iavg = iTotal/3"). Recomputing
 * it directly here keeps this report's numbers internally consistent (the
 * phase currents shown and the % shown now actually agree), independent of
 * that upstream bug until it's fixed for the whole app.
 */
function currentImbalancePctOf(row: TelemetryRow): number {
  const iR = safe(row.iR), iY = safe(row.iY), iB = safe(row.iB);
  const avg = (iR + iY + iB) / 3;
  if (avg <= 0) return 0;
  return (Math.max(Math.abs(iR - avg), Math.abs(iY - avg), Math.abs(iB - avg)) / avg) * 100;
}

function computeCurrentUnbalanceStats(
  active: TelemetryRow[], thresholdPct: number, harmonicCorrelated: boolean,
): CurrentUnbalanceStats | null {
  if (!active.length) return null;
  const pcts = active.map(currentImbalancePctOf);
  const aboveCount = pcts.filter(p => p > thresholdPct).length;

  let worstIdx = 0;
  for (let i = 1; i < active.length; i++) if (pcts[i] > pcts[worstIdx]) worstIdx = i;
  const worstRow = active[worstIdx];

  // How many readings this week came anywhere close to the worst one?
  // If it's just this single reading, it's an outlier, not a pattern.
  const nearWorstCount = pcts.filter(p => p >= pcts[worstIdx] * 0.8).length;
  const isSingletonEvent = nearWorstCount <= 1;

  const iR = safe(worstRow.iR), iY = safe(worstRow.iY), iB = safe(worstRow.iB);
  const avgAtWorst = (iR + iY + iB) / 3;
  const deviations: { name: 'R' | 'Y' | 'B'; dev: number }[] = [
    { name: 'R' as const, dev: Math.abs(iR - avgAtWorst) },
    { name: 'Y' as const, dev: Math.abs(iY - avgAtWorst) },
    { name: 'B' as const, dev: Math.abs(iB - avgAtWorst) },
  ].sort((a, b) => b.dev - a.dev);

  return {
    meanPct: round1(mean(pcts)),
    pctAboveThreshold: round1((aboveCount / pcts.length) * 100),
    thresholdPct,
    worstPct: round1(pcts[worstIdx]),
    exceededThreshold: pcts[worstIdx] > thresholdPct,
    worstPhaseR: round1(iR),
    worstPhaseY: round1(iY),
    worstPhaseB: round1(iB),
    worstAvgA: round1(avgAtWorst),
    contributingPhase: deviations[0].name,
    powerCorroborated: powerCorroboratesCurrentFault(worstRow, deviations[0].name),
    harmonicCorrelated,
    isSingletonEvent,
  };
}

interface LowPfPhaseStats {
  worstPhase: 'R' | 'Y' | 'B';
  worstPhaseMeanPf: number;
  pctBelowThreshold: number;
  /**
   * A SECOND phase that is also meaningfully affected (not just "the least
   * bad of the two others") — present only when its own % below threshold
   * clears SECOND_PHASE_SIGNIFICANT_PCT. Kept separate from `otherPhases` so
   * the narration can call it out explicitly instead of lumping it in with
   * the genuinely healthy phase.
   */
  secondaryPhase?: { name: 'R' | 'Y' | 'B'; meanPf: number; pctBelowThreshold: number };
  /** Phases that are genuinely fine — excludes any secondaryPhase above. */
  otherPhases: { name: 'R' | 'Y' | 'B'; meanPf: number }[];
  daysSpan: number;
  thresholdUsed: number;
}

/** "Chronically low" reporting threshold — deliberately the tighter 0.85 boundary
 *  (matches the HIGH-severity cut in detectAnomalies), not the softer 0.92 detection
 *  threshold, so this finding calls out the phase that's genuinely problematic. */
const LOW_PF_PHASE_THRESHOLD = 0.85;

/** Same "at least 1 in 10 readings" bar used elsewhere in this report before a
 *  finding is treated as noteworthy — applied here to the second-worst phase
 *  so it isn't silently folded into "the other phases stayed fine" when it's
 *  actually a second real problem, just slightly smaller than the worst one. */
const SECOND_PHASE_SIGNIFICANT_PCT = 10;

function computeLowPfPhaseStats(active: TelemetryRow[]): LowPfPhaseStats | null {
  if (!active.length) return null;
  const phaseStats = (['R', 'Y', 'B'] as const).map(name => {
    const vals = active
      .map(r => Math.abs(safe(name === 'R' ? r.pfR : name === 'Y' ? r.pfY : r.pfB)))
      .filter(v => v > 0);
    const belowCount = vals.filter(v => v < LOW_PF_PHASE_THRESHOLD).length;
    return { name, meanPf: mean(vals), pctBelow: vals.length ? (belowCount / vals.length) * 100 : 0, sampleCount: vals.length };
  });
  phaseStats.sort((a, b) => b.pctBelow - a.pctBelow);
  const worst = phaseStats[0];
  if (!worst.sampleCount || worst.pctBelow <= 0) return null;

  const second = phaseStats[1];
  const secondSignificant = !!second && second.sampleCount > 0 && second.pctBelow >= SECOND_PHASE_SIGNIFICANT_PCT;

  const daysSpan = new Set(active.map(r => r.date)).size;
  return {
    worstPhase: worst.name,
    worstPhaseMeanPf: Math.round(worst.meanPf * 1000) / 1000,
    pctBelowThreshold: round1(worst.pctBelow),
    secondaryPhase: secondSignificant
      ? { name: second.name, meanPf: Math.round(second.meanPf * 1000) / 1000, pctBelowThreshold: round1(second.pctBelow) }
      : undefined,
    otherPhases: phaseStats
      .slice(1)
      .filter(p => !(secondSignificant && p.name === second.name))
      .map(p => ({ name: p.name, meanPf: Math.round(p.meanPf * 1000) / 1000 })),
    daysSpan,
    thresholdUsed: LOW_PF_PHASE_THRESHOLD,
  };
}

interface VoltageUnbalanceStats {
  meanPct: number;
  pctAboveThreshold: number;
  thresholdPct: number;
  worstPct: number;
  /** Phase L-N voltages (V) at the single worst-imbalance reading this week. */
  worstPhaseR: number;
  worstPhaseY: number;
  worstPhaseB: number;
  worstAvgV: number;
  contributingPhase: 'R' | 'Y' | 'B';
}

function voltageImbalancePctOf(row: TelemetryRow): number {
  const vR = safe(row.vR), vY = safe(row.vY), vB = safe(row.vB);
  const avg = (vR + vY + vB) / 3;
  if (avg <= 0) return 0;
  return (Math.max(Math.abs(vR - avg), Math.abs(vY - avg), Math.abs(vB - avg)) / avg) * 100;
}

/** Same worst-single-reading breakdown style as computeCurrentUnbalanceStats,
 *  applied to supply voltage instead of current. */
function computeVoltageUnbalanceStats(active: TelemetryRow[], thresholdPct: number): VoltageUnbalanceStats | null {
  if (!active.length) return null;
  const pcts = active.map(voltageImbalancePctOf);
  const aboveCount = pcts.filter(p => p > thresholdPct).length;
  if (!aboveCount) return null;

  let worstIdx = 0;
  for (let i = 1; i < active.length; i++) if (pcts[i] > pcts[worstIdx]) worstIdx = i;
  const worstRow = active[worstIdx];

  const vR = safe(worstRow.vR), vY = safe(worstRow.vY), vB = safe(worstRow.vB);
  const avgAtWorst = (vR + vY + vB) / 3;
  const deviations: { name: 'R' | 'Y' | 'B'; dev: number }[] = [
    { name: 'R' as const, dev: Math.abs(vR - avgAtWorst) },
    { name: 'Y' as const, dev: Math.abs(vY - avgAtWorst) },
    { name: 'B' as const, dev: Math.abs(vB - avgAtWorst) },
  ].sort((a, b) => b.dev - a.dev);

  return {
    meanPct: round1(mean(pcts)),
    pctAboveThreshold: round1((aboveCount / pcts.length) * 100),
    thresholdPct,
    worstPct: round1(pcts[worstIdx]),
    worstPhaseR: round1(vR),
    worstPhaseY: round1(vY),
    worstPhaseB: round1(vB),
    worstAvgV: round1(avgAtWorst),
    contributingPhase: deviations[0].name,
  };
}

interface ThdPhaseStats {
  worstPhase: 'R' | 'Y' | 'B';
  worstPhaseMeanPct: number;
  worstPhasePeakPct: number;
  pctAboveThreshold: number;
  otherPhases: { name: 'R' | 'Y' | 'B'; meanPct: number }[];
  daysSpan: number;
  thresholdUsed: number;
}

/** Same per-phase breakdown style as computeLowPfPhaseStats, applied to
 *  harmonic distortion (voltage or current phases, picked by `field`). */
function computeThdPhaseStats(active: TelemetryRow[], thresholdPct: number, field: 'voltage' | 'current'): ThdPhaseStats | null {
  if (!active.length) return null;
  const getVal = (r: TelemetryRow, name: 'R' | 'Y' | 'B'): number => {
    if (field === 'voltage') return name === 'R' ? r.vRThdPct : name === 'Y' ? r.vYThdPct : r.vBThdPct;
    return name === 'R' ? r.iRThdPct : name === 'Y' ? r.iYThdPct : r.iBThdPct;
  };
  const phaseStats = (['R', 'Y', 'B'] as const).map(name => {
    const vals = active.map(r => safe(getVal(r, name))).filter(v => v > 0);
    const aboveCount = vals.filter(v => v > thresholdPct).length;
    return {
      name,
      meanPct: vals.length ? mean(vals) : 0,
      peakPct: vals.length ? max(vals) : 0,
      pctAbove: vals.length ? (aboveCount / vals.length) * 100 : 0,
      sampleCount: vals.length,
    };
  });
  phaseStats.sort((a, b) => b.pctAbove - a.pctAbove);
  const worst = phaseStats[0];
  if (!worst.sampleCount || worst.pctAbove <= 0) return null;

  return {
    worstPhase: worst.name,
    worstPhaseMeanPct: round1(worst.meanPct),
    worstPhasePeakPct: round1(worst.peakPct),
    pctAboveThreshold: round1(worst.pctAbove),
    otherPhases: phaseStats.slice(1).map(p => ({ name: p.name, meanPct: round1(p.meanPct) })),
    daysSpan: new Set(active.map(r => r.date)).size,
    thresholdUsed: thresholdPct,
  };
}

interface OverloadStats {
  meanPct: number;
  peakPct: number;
  pctAboveThreshold: number;
  thresholdPct: number;
  daysSpan: number;
}

function computeOverloadStats(active: TelemetryRow[], thresholdPct: number): OverloadStats | null {
  if (!active.length) return null;
  const vals = active.map(r => safe(r.loadPct)).filter(v => v > 0);
  if (!vals.length) return null;
  const aboveCount = vals.filter(v => v > thresholdPct).length;
  if (!aboveCount) return null;
  return {
    meanPct: round1(mean(vals)),
    peakPct: round1(max(vals)),
    pctAboveThreshold: round1((aboveCount / vals.length) * 100),
    thresholdPct,
    daysSpan: new Set(active.map(r => r.date)).size,
  };
}

interface FrequencyStats {
  meanHz: number;
  peakHz: number;
  peakDeviationHz: number;
  pctAboveThreshold: number;
  thresholdDeviationHz: number;
  direction: 'above' | 'below';
}

/** Frequency is grid-side, not load-dependent — evaluated over ALL eligible
 *  (non-dropout) rows, not just active-load rows like the others above. */
function computeFrequencyStats(eligible: TelemetryRow[], thresholdDeviationHz: number): FrequencyStats | null {
  const vals = eligible.map(r => safe(r.frequencyHz, 50)).filter(v => v > 0);
  if (!vals.length) return null;
  const deviations = vals.map(v => v - 50);
  const absDevs = deviations.map(Math.abs);
  const aboveCount = absDevs.filter(d => d > thresholdDeviationHz).length;
  if (!aboveCount) return null;

  let worstIdx = 0;
  for (let i = 1; i < vals.length; i++) if (absDevs[i] > absDevs[worstIdx]) worstIdx = i;

  return {
    meanHz: round1(mean(vals)),
    peakHz: round1(vals[worstIdx]),
    peakDeviationHz: round1(absDevs[worstIdx]),
    pctAboveThreshold: round1((aboveCount / vals.length) * 100),
    thresholdDeviationHz,
    direction: deviations[worstIdx] > 0 ? 'above' : 'below',
  };
}

/**
 * Recommended actions are scoped to this week's actual finding (which kind
 * of problem, roughly how widespread it was) rather than a static
 * one-line-per-kind template repeated every week. NOTE: the peak value and
 * % of readings are already shown in the explanation line directly above
 * this action in the UI — do NOT restate those same figures here, that's
 * pure repetition of the sentence the reader just read.
 */
function buildGenericAction(kind: string, pct: number): string {
  const extent = pct >= 90 ? 'This was present in nearly all of this week\'s readings, not an occasional blip' : 'This showed up in a meaningful share of readings, not just a one-off';
  switch (kind) {
    case 'voltageUnbalance':
      return `Phase voltage audit by a qualified electrical contractor — check transformer tap settings and supply-side connections for loose or unequal loading across phases. ${extent}.`;
    case 'overload':
      return `Review whether contracted/rated capacity still matches actual peak load; consider load scheduling changes or a contract demand revision. ${extent}.`;
    case 'frequency':
      return `Log the timestamps of frequency deviation events and share with your DISCOM — this is typically a grid-side condition, not a site fault. ${extent}.`;
    case 'highVoltageTHD':
    case 'highCurrentTHD':
      return `A harmonic spectrum (PQ) audit by a qualified power quality engineer is recommended to identify the dominant harmonic orders and size a filter appropriately. ${extent}.`;
    default:
      return `Have a qualified electrical contractor review this finding on-site. ${extent}.`;
  }
}

function buildHealthFindings(
  weekRows: TelemetryRow[],
  tariff: StateTariff,
  sheet: SheetSource,
): HealthFinding[] {
  const eligible = weekRows.filter(r => !r.isDropout);
  const totalEligible = eligible.length || 1;
  const active = activeRowsOf(weekRows);

  const counts: Record<string, { count: number; maxValue: number; severityRank: number; threshold: number }> = {};
  const rank = { WARNING: 1, HIGH: 2, CRITICAL: 3 } as Record<string, number>;

  for (const row of eligible) {
    for (const hit of detectAnomalies(row, DEFAULT_THRESHOLDS)) {
      if (hit.kind === 'dropout') continue;
      const cur = counts[hit.kind] ?? { count: 0, maxValue: 0, severityRank: 0, threshold: hit.threshold };
      cur.count += 1;
      if (hit.value > cur.maxValue) cur.maxValue = hit.value;
      cur.severityRank = Math.max(cur.severityRank, rank[hit.severity] ?? 0);
      counts[hit.kind] = cur;
    }
  }

  // A meaningful share of readings also showing high current THD alongside high
  // current imbalance is the physical signature of unevenly-distributed non-linear
  // loads (VFDs, drives, rectifiers) — only surface that reasoning when it's real.
  const harmonicCorrelated = (counts.highCurrentTHD?.count ?? 0) / totalEligible > 0.05;

  const phaseLoss = detectPhaseLossEvents(weekRows, tariff);

  const candidates: (HealthFinding & { score: number })[] = [];

  if (phaseLoss.count > 0) {
    candidates.push({
      kind: 'phaseLoss',
      title: 'Voltage phase loss under active load',
      explanation: `${phaseLoss.count} confirmed event${phaseLoss.count === 1 ? '' : 's'} this week where one supply voltage phase dropped below ${tariff.voltageDropoutV} V while production was running, including one event at an active load of ${phaseLoss.maxLoadKw} kW.`,
      action: `Thermographic scan and torque check of all incomer bus terminations — have a qualified electrical contractor inspect MCB/isolator contacts on the supply side and re-torque all connections. Independent verification is recommended before further intervention.`,
      stats: {
        'number of confirmed phase-loss events this week': String(phaseLoss.count),
        'the voltage level below which a phase is considered lost (V)': String(tariff.voltageDropoutV),
        'highest active load recorded during one of these events (kW)': String(phaseLoss.maxLoadKw),
      },
      narrativeFacts: [
        'This means one supply voltage phase briefly dropped out while the plant was actively drawing load, not while idle.',
      ],
      score: 30 + phaseLoss.count,
    });
  }

  for (const [kind, data] of Object.entries(counts)) {
    const pct = round1((data.count / totalEligible) * 100);
    const label = ({
      neutralCurrent: 'Elevated neutral current',
      voltageUnbalance: 'Voltage imbalance across phases',
      currentUnbalance: 'Current imbalance across phases',
      lowPF: 'Power factor chronically low',
      overload: 'Approaching contracted capacity',
      frequency: 'Grid frequency deviation',
      highVoltageTHD: 'High voltage harmonic distortion',
      highCurrentTHD: 'High current harmonic distortion',
    } as Record<string, string>)[kind] ?? kind;

    const unit = ({
      neutralCurrent: 'A', voltageUnbalance: '%', currentUnbalance: '%',
      lowPF: '', overload: '%', frequency: 'Hz', highVoltageTHD: '%', highCurrentTHD: '%',
    } as Record<string, string>)[kind] ?? '';

    // ── Rich, kind-specific stats (per the desired narration style) ─────────
    if (kind === 'neutralCurrent') {
      const s = computeNeutralCurrentStats(active);
      if (s) {
        candidates.push({
          kind, title: label,
          explanation: `Neutral current averaged ${s.meanA} A (peak ${s.peakA} A) during active operation this week. Neutral current exceeded the normal threshold of ${s.flatThresholdA} A in ${s.pctAboveFlatThreshold}% of readings, and ${s.pctAboveTenPctPhaseAvg}% of readings showed neutral current above 10% of the average phase current.`,
          action: `Have a qualified electrical contractor verify neutral conductor sizing against the current levels actually being carried this week.${harmonicCorrelated ? ' A meaningful share of readings this week also showed elevated current harmonic distortion, which independently drives up neutral current — mitigating that will help too.' : ''}`,
          stats: {
            'mean neutral current during active operation (A)': `${s.meanA} A`,
            'peak neutral current recorded during active operation (A)': `${s.peakA} A`,
            'the normal/warning neutral current threshold (A)': `${s.flatThresholdA} A`,
            'percentage of readings where neutral current exceeded the normal threshold': `${s.pctAboveFlatThreshold}%`,
            'percentage of readings where neutral current exceeded 10% of the average phase current': `${s.pctAboveTenPctPhaseAvg}%`,
          },
          narrativeFacts: [
            'In a balanced, clean 3-phase system the neutral current should be near zero — elevated neutral current usually means unbalanced single-phase loads or harmonic content.',
            `State plainly that neutral current exceeded the normal threshold of ${s.flatThresholdA} A in ${s.pctAboveFlatThreshold}% of readings — lead with this figure before the relative (10% of phase average) figure.`,
          ],
          score: data.severityRank * 10 + pct,
        });
        continue;
      }
    }

    if (kind === 'currentUnbalance') {
      const s = computeCurrentUnbalanceStats(active, DEFAULT_THRESHOLDS.currentUnbalancePct, harmonicCorrelated);
      if (s) {
        // A single isolated 15-min reading is weak evidence of a real fault
        // even when power happens to line up with it — treat it with the same
        // caution as a non-corroborated reading, since it could just as
        // easily be a one-off sensor/data blip rather than a recurring issue.
        const needsVerification = !s.powerCorroborated || s.isSingletonEvent;
        candidates.push({
          kind, title: needsVerification ? `${label} (verify sensor)` : label,
          explanation: `At the worst point this week, Phase R carried ${s.worstPhaseR} A, Phase Y carried ${s.worstPhaseY} A, and Phase B carried ${s.worstPhaseB} A — averaging ${s.worstAvgA} A across the three phases. That is a ${s.worstPct}% imbalance, ${s.exceededThreshold ? 'exceeding' : 'staying within'} the ${s.thresholdPct}% threshold, with Phase ${s.contributingPhase} contributing most to the imbalance.${s.isSingletonEvent ? ` This was the only reading this week that looked like this — every other reading stayed well below it, so treat it as an isolated reading to verify rather than a recurring pattern.` : ''}${!s.powerCorroborated ? ` Note: Phase ${s.contributingPhase}'s power reading did not drop correspondingly at that same moment, so this may be a current-sensor reading issue rather than a confirmed fault — worth verifying the sensor before assuming a real event.` : ''}`,
          action: needsVerification
            ? `Have a qualified electrical contractor first verify the current sensor/CT on Phase ${s.contributingPhase}${s.isSingletonEvent ? ' — this reading only showed up once all week, which points more toward a sensor or data-logging glitch than a real recurring fault' : ' — its power reading did not move correspondingly with the current reading at the worst moment, which points to a possible sensor fault'}. Only proceed with load rebalancing once the reading is confirmed accurate.`
            : `Load rebalancing assessment focused on Phase ${s.contributingPhase} — redistribute single-phase loads across R, Y, B phases to bring imbalance back within the threshold, typically feasible during a planned maintenance window.`,
          stats: {
            'Phase R current at the worst imbalance reading this week (A)': `${s.worstPhaseR} A`,
            'Phase Y current at the worst imbalance reading this week (A)': `${s.worstPhaseY} A`,
            'Phase B current at the worst imbalance reading this week (A)': `${s.worstPhaseB} A`,
            'average current across the three phases at that same reading (A)': `${s.worstAvgA} A`,
            'the imbalance percentage at that reading': `${s.worstPct}%`,
            'the imbalance threshold used (percent)': `${s.thresholdPct}%`,
            'the single phase that contributed most to that imbalance': `Phase ${s.contributingPhase}`,
            'mean current imbalance across all active periods this week (percent)': `${s.meanPct}%`,
            'percentage of readings above the imbalance threshold': `${s.pctAboveThreshold}%`,
          },
          narrativeFacts: [
            s.exceededThreshold
              ? `This exceeds the ${s.thresholdPct}% threshold.`
              : `This stayed within the ${s.thresholdPct}% threshold.`,
            s.isSingletonEvent
              ? `This was the single most extreme reading of the whole week, with no other reading anywhere close to it — treat it as an isolated data point to verify, not a confirmed recurring fault, regardless of what the power reading shows at that same instant.`
              : s.powerCorroborated
                ? `Phase ${s.contributingPhase}'s power reading also dropped/rose correspondingly at that same moment, consistent with a real electrical event rather than a sensor glitch.`
                : `Phase ${s.contributingPhase}'s power reading did NOT move correspondingly at that same moment — flag this as a possible current-sensor (CT) reading issue on that phase rather than a confirmed fault, and recommend verifying the sensor before treating it as a real event.`,
            ...(s.harmonicCorrelated
              ? ['This week also showed meaningfully elevated current harmonic distortion — combined with the imbalance, this points to non-linear loads (VFDs, motor drives, rectifiers) unevenly distributed across the three phases.']
              : []),
          ],
          score: data.severityRank * 10 + pct,
        });
        continue;
      }
    }

    if (kind === 'lowPF') {
      const s = computeLowPfPhaseStats(active);
      if (s) {
        // "Chronically low" should only describe a phase whose AVERAGE PF is
        // itself below threshold — not a phase that stays fine on average but
        // occasionally dips. Each affected phase gets its own honest wording.
        const worstChronic = s.worstPhaseMeanPf < s.thresholdUsed;
        const secondaryChronic = !!s.secondaryPhase && s.secondaryPhase.meanPf < s.thresholdUsed;

        const phaseClause = (name: string, meanPf: number, pctBelow: number, chronic: boolean): string =>
          chronic
            ? `Phase ${name}'s average power factor was itself below ${s.thresholdUsed} this week (at ${meanPf}), staying under threshold in ${pctBelow}% of operating readings across ${s.daysSpan} days`
            : `Phase ${name}'s power factor dipped below ${s.thresholdUsed} in ${pctBelow}% of operating readings across ${s.daysSpan} days this week, though it averaged ${meanPf} overall — above the threshold`;

        const othersText = s.otherPhases.map(p => `Phase ${p.name} at ${p.meanPf}`).join(' and ');
        const secondaryClause = s.secondaryPhase
          ? ` ${phaseClause(s.secondaryPhase.name, s.secondaryPhase.meanPf, s.secondaryPhase.pctBelowThreshold, secondaryChronic)}.`
          : '';

        const chronicNames = [
          worstChronic ? s.worstPhase : null,
          secondaryChronic && s.secondaryPhase ? s.secondaryPhase.name : null,
        ].filter((n): n is 'R' | 'Y' | 'B' => n !== null);

        const title = chronicNames.length > 0
          ? `Phase ${chronicNames.join(' and Phase ')} power factor chronically low`
          : `Phase ${s.worstPhase}${s.secondaryPhase ? ` and Phase ${s.secondaryPhase.name}` : ''} power factor dips below threshold`;

        const affectedPhases = s.secondaryPhase ? `Phase ${s.worstPhase} and Phase ${s.secondaryPhase.name}` : `Phase ${s.worstPhase}`;
        const capacitorNote = chronicNames.length > 0
          ? `Since the average is genuinely low on ${chronicNames.length > 1 ? 'both phases' : 'that phase'}, a permanently wired capacitor bank sized for the typical reactive load is likely worthwhile.`
          : `Since the average stays above ${s.thresholdUsed} and it's the occasional dip pulling this down, first check what's switching on during those dips before committing to fixed capacitor sizing.`;

        candidates.push({
          kind, title,
          explanation: `${phaseClause(s.worstPhase, s.worstPhaseMeanPf, s.pctBelowThreshold, worstChronic)}, while ${othersText}.${secondaryClause}`,
          action: `Reactive load audit focused on ${affectedPhases} by a qualified electrical contractor — inspect single-phase inductive loads (motors, ballasts) wired predominantly to ${chronicNames.length > 1 ? 'these phases' : 'that phase'}. ${capacitorNote}`,
          stats: {
            'the most-affected phase': `Phase ${s.worstPhase}`,
            'the low-power-factor threshold used': String(s.thresholdUsed),
            'percentage of operating readings where the most-affected phase\'s power factor was below the threshold': `${s.pctBelowThreshold}%`,
            'number of days spanned this week': String(s.daysSpan),
            'the most-affected phase\'s mean power factor this week': String(s.worstPhaseMeanPf),
            ...(s.secondaryPhase ? {
              [`the second-affected phase (Phase ${s.secondaryPhase.name}): percentage of readings below the threshold`]: `${s.secondaryPhase.pctBelowThreshold}%`,
              [`the second-affected phase (Phase ${s.secondaryPhase.name}): mean power factor this week`]: String(s.secondaryPhase.meanPf),
            } : {}),
            ...Object.fromEntries(s.otherPhases.map(p => [`Phase ${p.name}'s mean power factor this week (for comparison — this phase stayed fine)`, String(p.meanPf)])),
          },
          narrativeFacts: [
            worstChronic
              ? `Phase ${s.worstPhase}'s average power factor is genuinely below threshold this week — this is a persistent ("chronic") condition, safe to describe as chronically low.`
              : `Phase ${s.worstPhase}'s average power factor stayed ABOVE the threshold this week even though it dipped below sometimes — do NOT call this "chronically low"; describe it as occasional dips instead.`,
            ...(s.secondaryPhase ? [
              secondaryChronic
                ? `Phase ${s.secondaryPhase.name}'s average power factor is also genuinely below threshold — also safe to describe as chronically low.`
                : `Phase ${s.secondaryPhase.name}'s average power factor stayed ABOVE the threshold too — describe it as occasional dips, not chronically low.`,
            ] : []),
            `This pattern suggests reactive load concentrated on Phase ${s.worstPhase}${s.secondaryPhase ? ` and Phase ${s.secondaryPhase.name}` : ''} — likely single-phase inductive loads (motors, ballasts) predominantly wired to ${s.secondaryPhase ? 'those phases' : 'that phase'}.`,
          ],
          score: data.severityRank * 10 + pct,
        });
        continue;
      }
    }

    if (kind === 'voltageUnbalance') {
      const s = computeVoltageUnbalanceStats(active, data.threshold);
      if (s) {
        candidates.push({
          kind, title: label,
          explanation: `At the worst point this week, Phase R measured ${s.worstPhaseR} V, Phase Y measured ${s.worstPhaseY} V, and Phase B measured ${s.worstPhaseB} V — averaging ${s.worstAvgV} V across the three phases. That is a ${s.worstPct}% imbalance against the ${s.thresholdPct}% threshold, with Phase ${s.contributingPhase} deviating most. Across the week, imbalance averaged ${s.meanPct}%, exceeding threshold in ${s.pctAboveThreshold}% of readings.`,
          action: `Phase voltage audit by a qualified electrical contractor focused on Phase ${s.contributingPhase} — check transformer tap settings and supply-side connections for loose or unequal loading across phases.`,
          stats: {
            'Phase R voltage at the worst imbalance reading this week (V)': `${s.worstPhaseR} V`,
            'Phase Y voltage at the worst imbalance reading this week (V)': `${s.worstPhaseY} V`,
            'Phase B voltage at the worst imbalance reading this week (V)': `${s.worstPhaseB} V`,
            'average voltage across the three phases at that same reading (V)': `${s.worstAvgV} V`,
            'the imbalance percentage at that reading': `${s.worstPct}%`,
            'the imbalance threshold used (percent)': `${s.thresholdPct}%`,
            'the phase that deviated most at that reading': `Phase ${s.contributingPhase}`,
            'mean voltage imbalance across the week (percent)': `${s.meanPct}%`,
            'percentage of readings above the imbalance threshold': `${s.pctAboveThreshold}%`,
          },
          narrativeFacts: [
            'Sustained voltage imbalance across phases causes uneven heating inside three-phase motors and shortens their working life.',
            'Voltage imbalance is usually caused on the supply side (transformer tap, unequal single-phase loading upstream) rather than by equipment inside the plant.',
          ],
          score: data.severityRank * 10 + pct,
        });
        continue;
      }
    }

    if (kind === 'highVoltageTHD' || kind === 'highCurrentTHD') {
      const isVoltage = kind === 'highVoltageTHD';
      const s = computeThdPhaseStats(active, data.threshold, isVoltage ? 'voltage' : 'current');
      if (s) {
        const othersText = s.otherPhases.map(p => `Phase ${p.name} at ${p.meanPct}%`).join(' and ');
        candidates.push({
          kind, title: `${isVoltage ? 'Voltage' : 'Current'} harmonic distortion concentrated on Phase ${s.worstPhase}`,
          explanation: `Phase ${s.worstPhase} shows the highest ${isVoltage ? 'voltage' : 'current'} harmonic distortion this week — averaging ${s.worstPhaseMeanPct}% and peaking at ${s.worstPhasePeakPct}%, above the ${s.thresholdUsed}% threshold in ${s.pctAboveThreshold}% of readings across ${s.daysSpan} days, while ${othersText}.`,
          action: `A harmonic spectrum (PQ) audit by a qualified power quality engineer, focused on Phase ${s.worstPhase} — identify the dominant harmonic orders and size a filter appropriately. ${isVoltage ? 'Voltage distortion this concentrated on one phase often traces back to a non-linear load fed from that same phase upstream.' : 'This pattern typically points to a non-linear load (VFD, drive, rectifier) wired predominantly to that phase.'}`,
          stats: {
            'the most-affected phase': `Phase ${s.worstPhase}`,
            [`${isVoltage ? 'voltage' : 'current'} THD threshold used (percent)`]: `${s.thresholdUsed}%`,
            'the most-affected phase\'s mean THD this week (percent)': `${s.worstPhaseMeanPct}%`,
            'the most-affected phase\'s peak THD this week (percent)': `${s.worstPhasePeakPct}%`,
            'percentage of readings where the most-affected phase exceeded the threshold': `${s.pctAboveThreshold}%`,
            'number of days spanned this week': String(s.daysSpan),
            ...Object.fromEntries(s.otherPhases.map(p => [`Phase ${p.name}'s mean THD this week (for comparison)`, `${p.meanPct}%`])),
          },
          narrativeFacts: [
            isVoltage
              ? 'Elevated voltage harmonic distortion points to non-linear loads on the supply and can cause extra heating in transformers and cabling over time.'
              : 'Elevated current harmonic distortion typically comes from non-linear loads (VFDs, drives, rectifiers) and adds extra heating losses in cables and transformers.',
          ],
          score: data.severityRank * 10 + pct,
        });
        continue;
      }
    }

    if (kind === 'overload') {
      const s = computeOverloadStats(active, data.threshold);
      if (s) {
        candidates.push({
          kind, title: label,
          explanation: `Load averaged ${s.meanPct}% of rated capacity this week, peaking at ${s.peakPct}% — above the ${s.thresholdPct}% threshold in ${s.pctAboveThreshold}% of readings across ${s.daysSpan} days.`,
          action: `Review whether contracted/rated capacity still matches actual peak load — consider load scheduling changes or a contract demand revision. ${s.peakPct > 100 ? 'Peak load this week actually exceeded 100% of rated capacity, which risks nuisance tripping.' : 'Peak load stayed under rated capacity but consistently close to it, leaving little headroom.'}`,
          stats: {
            'average load this week (percent of rated capacity)': `${s.meanPct}%`,
            'peak load this week (percent of rated capacity)': `${s.peakPct}%`,
            'the overload threshold used (percent)': `${s.thresholdPct}%`,
            'percentage of readings above the threshold': `${s.pctAboveThreshold}%`,
            'number of days spanned this week': String(s.daysSpan),
          },
          narrativeFacts: [
            'Running close to or above rated/contracted capacity risks nuisance tripping of protective devices and accelerates wear on cables and switchgear.',
          ],
          score: data.severityRank * 10 + pct,
        });
        continue;
      }
    }

    if (kind === 'frequency') {
      const s = computeFrequencyStats(eligible, DEFAULT_THRESHOLDS.frequencyDeviationHz);
      if (s) {
        candidates.push({
          kind, title: label,
          explanation: `Grid frequency averaged ${s.meanHz} Hz this week, reaching ${s.peakHz} Hz at its most extreme — a ${s.peakDeviationHz} Hz deviation from the 50 Hz nominal, above the ${s.thresholdDeviationHz} Hz threshold in ${s.pctAboveThreshold}% of readings. The largest deviation was ${s.direction} 50 Hz.`,
          action: `Log the timestamps of these frequency deviation events and share with your DISCOM — this is typically a grid-side condition, not a site fault, so there is little to fix on-site beyond documenting it.`,
          stats: {
            'mean grid frequency this week (Hz)': `${s.meanHz} Hz`,
            'peak frequency reading this week (Hz)': `${s.peakHz} Hz`,
            'the largest deviation from 50 Hz nominal this week (Hz)': `${s.peakDeviationHz} Hz`,
            'the frequency deviation threshold used (Hz)': `${s.thresholdDeviationHz} Hz`,
            'percentage of readings exceeding the deviation threshold': `${s.pctAboveThreshold}%`,
          },
          narrativeFacts: [
            'Frequency deviation from 50 Hz is almost always a grid-side condition, not caused on-site — it can still affect the speed consistency of motors and drives.',
          ],
          score: data.severityRank * 10 + pct,
        });
        continue;
      }
    }

    // ── Generic fallback for kinds without a bespoke breakdown above ─────────
    // Even without a fully bespoke breakdown, always show the engineering
    // threshold being compared against and what the physical implication is —
    // a bare "detected in X%, peak Y%" tells the reader nothing about whether
    // that number is actually meaningful or what to do about it.
    const peakStr = `${rupees(data.maxValue)}${unit ? ' ' + unit : ''}`;
    const thresholdStr = kind === 'frequency'
      ? `${DEFAULT_THRESHOLDS.frequencyDeviationHz} Hz deviation from the 50 Hz nominal`
      : `${data.threshold}${unit}`;
    const meaning = ({
      voltageUnbalance: 'Sustained voltage imbalance across phases causes uneven heating inside three-phase motors and shortens their working life — the more consistently it stays above threshold, the more that risk compounds.',
      overload: 'Running close to or above rated/contracted capacity risks nuisance tripping of protective devices and accelerates wear on cables and switchgear.',
      frequency: 'Frequency deviation from 50 Hz is almost always a grid-side condition (not caused on-site) — it can still affect the speed consistency of motors and drives.',
      highVoltageTHD: 'Elevated voltage harmonic distortion points to non-linear loads on the supply and can cause extra heating in transformers and cabling over time.',
      highCurrentTHD: 'Elevated current harmonic distortion typically comes from non-linear loads (VFDs, drives, rectifiers) and adds extra heating losses in cables and transformers.',
    } as Record<string, string>)[kind];
    candidates.push({
      kind,
      title: label,
      explanation: `Detected in ${pct}% of eligible readings this week (${data.count} of ${totalEligible}), peaking at ${peakStr} against the ${thresholdStr} threshold used to flag this.`,
      action: buildGenericAction(kind, pct),
      stats: {
        'percentage of eligible readings this week where this was detected': `${pct}%`,
        'peak value recorded this week': peakStr,
        'the engineering threshold used to flag this': thresholdStr,
      },
      narrativeFacts: meaning ? [meaning] : [],
      score: data.severityRank * 10 + pct,
    });
  }

  // Findings that run on registers the dashboard already ingests but never read
  // (feasibility assessment features 24, 22 and the aggregate half of 8). Each
  // returns null when its register is absent — 24 works on every meter model,
  // 22 and 8 only where the LK4410 extended block is reported.
  const registerFindings = [
    standbyFloorFinding(weekRows, {
      nonProdWindows: sheet.meta?.nonProdWindows,
      pEssentialKw: sheet.meta?.pEssentialKw,
      rateRsPerUnit: tariff.energyRates.NORMAL,
      rateUnit: tariff.energyUnit === 'kvah' ? 'kVAh' : 'kWh',
    }),
    leadingPfFinding(weekRows, { qStepMinKvar: sheet.meta?.qStepMinKvar }),
    voltageThdFinding(weekRows),
    capacitorFadeFinding(weekRows, {
      stages: sheet.meta?.apfcStages ?? [],
      vRatedV: sheet.meta?.vRatedV,
    }),
    utilizationFinding(weekRows, { pIdleKw: sheet.meta?.pIdleKw }),
    pfBasisFinding(weekRows),
  ];
  for (const f of registerFindings) if (f) candidates.push(f);

  // Two populations, ranked separately and on incompatible scales.
  //
  // The live-anomaly findings above score `severityRank * 10 + prevalence`, so a
  // condition present in most readings reaches ~130. The register findings score
  // 10-35 by design, because their magnitude is a rupee or a percentage rather
  // than a share of readings. Ranking them in one list does not compare like
  // with like — it just guarantees the register findings never surface on any
  // site that has a persistent electrical anomaly, which is most of them.
  //
  // Rescaling the register findings upward would be worse: a 100%-prevalence
  // neutral-current breach genuinely outranks standby load, and burying it to
  // make room would be a safety regression. So each population keeps its own
  // slots — the electrical picture is unchanged, and the efficiency and asset
  // findings get room of their own.
  const registerKinds = new Set([
    'standbyFloor', 'leadingPf', 'capacitorFade', 'utilization', 'pfBasis', 'voltageThd',
  ]);

  const ranked = [...candidates].sort((a, b) => b.score - a.score);
  const strip = ({ score: _score, ...rest }: HealthFinding & { score: number }) => rest;

  return [
    ...ranked.filter(c => !registerKinds.has(c.kind)).slice(0, 3).map(strip),
    ...ranked.filter(c =>  registerKinds.has(c.kind)).slice(0, 2).map(strip),
  ];
}

// ─── Top-level assembly ───────────────────────────────────────────────────────

export function buildWeeklyReportPayload(params: {
  allRows: TelemetryRow[];
  sheet: SheetSource;
  tariff: StateTariff;
  contractDemandKva: number;
  reportWeek: DateWindow;
  comparisonWeek: DateWindow;
}): WeeklyReportPayload {
  const { allRows, sheet, tariff, contractDemandKva, reportWeek, comparisonWeek } = params;

  const inWindow = (w: DateWindow) =>
    sanitiseWindow(allRows.filter(r => r.timestamp >= w.from && r.timestamp <= w.to));

  const weekRows = inWindow(reportWeek);
  const lastWeekRows = inWindow(comparisonWeek);

  const weeklyBreakdown = buildBillBreakdown(weekRows, undefined, undefined, contractDemandKva, tariff);
  const lastWeeklyBreakdown = buildBillBreakdown(lastWeekRows, undefined, undefined, contractDemandKva, tariff);

  const pf = buildPfSection(weekRows, tariff, weeklyBreakdown);
  const cd = buildCdSection(weekRows, tariff, contractDemandKva);
  const lf = buildLfSection(weekRows, tariff, weeklyBreakdown);

  const lastWeekCd = buildCdSection(lastWeekRows, tariff, contractDemandKva);

  // Net from the three billing sections above only:
  //   PF actualImpactRs (+ rebate / − surcharge)
  //   LF actualImpactRs (+ rebate / − surcharge)
  //   CD penaltyRs (a cost → subtract so it counts as − impact)
  // Never invent a ₹ figure the sections didn't already compute.
  const thisWeekTotalImpactRs = rupees(pf.actualImpactRs + lf.actualImpactRs - cd.penaltyRs);
  const lastWeekPfImpactRs = tariff.hasPfAdjustment ? rupees(-lastWeeklyBreakdown.pfAdjTotalRs) : 0;
  const lastWeekLfImpactRs = tariff.hasLfRebate ? rupees(-lastWeeklyBreakdown.lfRebateRs) : 0;
  const lastWeekTotalImpactRs = rupees(lastWeekPfImpactRs + lastWeekLfImpactRs - lastWeekCd.penaltyRs);
  const hasRebateSurchargeImpact =
    tariff.hasPfAdjustment ||
    tariff.hasLfRebate ||
    cd.penaltyRs > 0 ||
    lastWeekCd.penaltyRs > 0;

  const comparison = buildComparisonSection(
    weeklyBreakdown.pfWeighted, lastWeeklyBreakdown.pfWeighted,
    weeklyBreakdown.lfPct, lastWeeklyBreakdown.lfPct,
    cd.avgUtilizationPct, lastWeekCd.avgUtilizationPct,
    thisWeekTotalImpactRs,
    lastWeekTotalImpactRs,
    hasRebateSurchargeImpact,
    lastWeekRows.length > 0,
  );

  const healthFindings = buildHealthFindings(weekRows, tariff, sheet);

  return {
    generatedAt: new Date().toISOString(),
    reportWeekLabel: reportWeek.label,
    facility: buildFacilityProfile(sheet, tariff, contractDemandKva),
    billing: { pf, cd, lf, comparison },
    healthFindings,
  };
}
